import { useMemo, useState } from "react";
import { Meeting } from "../../../core/types/Meeting.model";
import { useData } from "../../../core/contexts/DataContext";
import { formatTime, getMeetingDateStr } from "../../../utils/dateUtils";
import { AnimatePresence, motion } from "framer-motion";
import { useDownloadFile } from "../../../core/hooks/useDownloadFile";
import { TypingDots } from "../../../share/components/TypingDots/TypingDots";
import { Popup } from "../../../share/components/Popup.tsx/Popup";
import { SalesPerson } from "../../../core/types/SalesPerson.model";

interface MyMeetingProps {
  meeting: Meeting;
}

const MyMeeting: React.FC<MyMeetingProps> = ({ meeting }) => {
  const { exhibitors, getExhibitorDetails, cancelBookedMeeting } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [openSalesPerson, setOpenSalesPerson] = useState<SalesPerson | null>(null);
  const exhibitor = useMemo(() => exhibitors.find(e => e.id === meeting.exhibitorId)!, [meeting, exhibitors]);

  const { handleDownloadIcs } = useDownloadFile();

  const handleViewDetails = () => {
    if (isOpen) setIsOpen(false);

    else {
      setIsOpen(true);
      getExhibitorDetails(exhibitor);
    }
  }

  const handleAddToCalendar = () => {
    handleDownloadIcs(meeting.id);
  }

  const handleCancelMeeting = () => {
    setIsDeletePopupOpen(true);
  }

  const handleShowSalesPersonDetails = (salesPerson: SalesPerson) => {
    if(salesPerson.bio) setOpenSalesPerson(salesPerson); }


  const meetingHeader = useMemo(() => `${formatTime(meeting.startTime)} - ${formatTime(meeting.endTime)}`, [meeting]);

  return (
    <div className="col-12" key={meeting.id} >
      <div className="card border-primary">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center mb-2 flex-wrap" >
                <h5 className="card-title fw-semibold text-dark mb-0 me-3">
                  {exhibitor?.origName}
                </h5>
                {meeting?.salesPersons?.map((salesPerson, index) => (

                  <span onClick={() => handleShowSalesPersonDetails(salesPerson)}
                    key={index}
                    className="badge bg-primary"
                    style={{ whiteSpace: 'normal', cursor: salesPerson.bio ? 'pointer' : 'default' }}>
                    {salesPerson.fullName}
                    {salesPerson.bio && <i className="ps-3 bi bi-info-circle"></i>}
                  </span>

                )
                )}
              </div>
              <p className="text-muted small mb-2">
                Booth {exhibitor?.boothNo} • {exhibitor?.origName}
              </p>
              <div className="d-flex align-items-center gap-2 gap-sm-4 flex-wrap">
                <div className="d-flex align-items-center">
                  <i className="bi bi-calendar-event text-primary me-2"></i>
                  <span className="small fw-medium">{getMeetingDateStr(meeting.startTime)}</span>
                </div>
                <div className="d-flex align-items-center">
                  <i className="bi bi-clock text-primary me-2"></i>
                  <span className="small fw-medium">{meetingHeader} </span>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <div className="d-flex flex-column gap-2">
                <button
                  onClick={() => handleAddToCalendar()}
                  className="btn btn-outline-primary btn-sm"
                  title=" Add Meeting to Calendar"
                >
                  <i className="bi bi-calendar-check me-1"></i>
                  Add to Calendar
                </button>
                <button
                  onClick={() => handleCancelMeeting()}
                  className="btn btn-outline-danger btn-sm"
                  title="Cancel Meeting"
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Cancel
                </button>
                <button
                  onClick={() => handleViewDetails()}
                  className="btn btn-outline-primary btn-sm"
                  title="View Details"
                >
                  <i className="bi bi-info-circle me-1"></i>
                  Company Details 
                </button>
              </div>
            </div>
          </div>
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key="content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="overflow-hidden"
              >
                {exhibitor.details &&
                  <div className="p-3 pt-4 text-sm text-gray-700 border-t border-gray-100 text-justify">
                    {exhibitor.details}
                  </div>
                }
                {!exhibitor.details && <TypingDots />}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
      {isDeletePopupOpen &&
        <Popup closeAction={({
          label: 'Close',
          className: 'btn-primary',
          callback: () => setIsDeletePopupOpen(false)
        })}

          actions={[{
            label: 'Cancel Meeting',
            className: 'btn-danger',
            callback: () => cancelBookedMeeting(meeting)
          }]}
          additionalClass=" border border-danger-subtle"
        >
          <div className="p-4 bg-light rounded-4 shadow-sm">
            <h2 className="text-danger fw-semibold mb-4 d-flex align-items-center">
              Are you sure you wish to cancel this meeting ?
            </h2>

            <div className="d-flex align-items-center mb-3 flex-wrap">
              <h5 className="card-title fw-semibold text-dark mb-0 me-3">
                {exhibitor?.origName}
              </h5>

              {meeting?.salesPersons?.map((salesPerson, index) => (
                <span
                  key={index}
                  className="badge bg-primary text-light me-1 mb-1"
                  style={{ whiteSpace: "normal" }}
                >
                  {salesPerson.fullName}
                </span>
              ))}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
              <div className="d-flex align-items-center text-secondary">
                <i className="bi bi-calendar-event text-primary me-2"></i>
                <span className="small fw-medium">{getMeetingDateStr(meeting.startTime)}</span>
              </div>

              <div className="d-flex align-items-center text-secondary">
                <i className="bi bi-clock text-primary me-2"></i>
                <span className="small fw-medium">{meetingHeader}</span>
              </div>
            </div>

            <div className="alert alert-warning py-2 px-3 small mb-0">
              <i className="bi bi-info-circle-fill me-2"></i>
              Please note:  once cancelled, this meeting cannot be restored.
            </div>
          </div>


        </Popup>}

      {openSalesPerson && (
        <Popup
          closeAction={{
            label: "Close",
            className: "btn-primary",
            callback: () => setOpenSalesPerson(null), 
          }}
          additionalClass="border border-info-subtle"
        >
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <h5 className="m-0 fw-semibold text-dark">{openSalesPerson.fullName}</h5>
          </div>

          {/* Body */}
          <div className="px-3 py-3">
            {openSalesPerson.bio ? (
              <div className="text-secondary small">
                {openSalesPerson.bio}
              </div>
            ) : (
              <div className="text-muted small fst-italic">No bio available</div>
            )}
          </div>
        </Popup>
      )}



    </div>

  )
}

export default MyMeeting;